import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Plus, RefreshCw, Trash2, Loader2, AlertTriangle, Stethoscope, ChevronDown, ChevronUp, Quote, Briefcase } from "lucide-react";
import { toast } from "sonner";

// ─── Constants ────────────────────────────────────────────────────────────────

const TRAJECTORY_STYLES = {
    critical: "bg-red-100 text-red-800 border-red-300",
    deteriorating: "bg-orange-100 text-orange-800 border-orange-300",
    watch: "bg-amber-100 text-amber-800 border-amber-300",
    stable: "bg-slate-100 text-slate-700 border-slate-300",
};

// Tier -> badge style, shared across liquidity/structural/profitability
// tiers even though the tier vocabularies differ (healthy/tight/strained,
// negative_net_worth/high_leverage/..., narrative_flagged/insufficient_data)
// — grouped by severity intent rather than one map per dimension.
const TIER_STYLES = {
    healthy: "bg-green-100 text-green-800 border-green-300",
    low_leverage: "bg-green-100 text-green-800 border-green-300",
    adequate: "bg-green-100 text-green-800 border-green-300",
    tight: "bg-amber-100 text-amber-800 border-amber-300",
    moderate_leverage: "bg-amber-100 text-amber-800 border-amber-300",
    narrative_flagged: "bg-amber-100 text-amber-800 border-amber-300",
    strained: "bg-red-100 text-red-800 border-red-300",
    negative_net_worth: "bg-red-100 text-red-800 border-red-300",
    high_leverage: "bg-red-100 text-red-800 border-red-300",
    insufficient_data: "bg-slate-100 text-slate-600 border-slate-300",
};

const TIER_LABELS = {
    negative_net_worth: "negative net worth",
    high_leverage: "high leverage",
    moderate_leverage: "moderate leverage",
    low_leverage: "low leverage",
    narrative_flagged: "narrative flagged",
    insufficient_data: "insufficient data",
};

function TrajectoryBadge({ trajectory }) {
    const cls = TRAJECTORY_STYLES[trajectory] || TRAJECTORY_STYLES.stable;
    return (
        <Badge variant="outline" className={cls} data-testid={`trajectory-${trajectory}`}>
            {trajectory}
        </Badge>
    );
}

function TierBadge({ tier }) {
    const cls = TIER_STYLES[tier] || TIER_STYLES.insufficient_data;
    const label = TIER_LABELS[tier] || tier;
    return (
        <Badge variant="outline" className={cls} data-testid={`tier-${tier}`}>
            {label}
        </Badge>
    );
}

function fmtDate(iso) {
    if (!iso) return "Never";
    try {
        return new Date(iso).toLocaleString("en-GB");
    } catch {
        return iso;
    }
}

function fmtGBP(n) {
    if (n == null) return "—";
    return `£${Number(n).toLocaleString("en-GB")}`;
}

const RED_FLAG_LABELS = {
    going_concern: "Going concern",
    declining_trend: "Declining trend",
    working_capital_strain: "Working capital strain",
    restructuring_refinancing: "Restructuring / refinancing",
    profitability: "Profitability",
};

// ─── Diagnosis panel ────────────────────────────────────────────────────────

function DiagnosisPanel({ diagnosis }) {
    if (!diagnosis) return null;

    if (!diagnosis.accounts_available) {
        return (
            <div className="mt-3 border-t pt-3 text-sm text-muted-foreground flex items-start gap-2" data-testid="diagnosis-unavailable">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                <span>{diagnosis.error || "No diagnosis data available for this company."}</span>
            </div>
        );
    }

    return (
        <div className="mt-3 border-t pt-3 space-y-4" data-testid="diagnosis-results">
            <p className="text-xs text-muted-foreground">
                Filed accounts as at {diagnosis.period_end_date || "unknown period end"}
                {diagnosis.filing_date ? ` (filed ${diagnosis.filing_date})` : ""} · diagnosed {fmtDate(diagnosis.diagnosed_at)}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-md border p-3" data-testid="diagnosis-liquidity">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-muted-foreground">Liquidity</span>
                        <TierBadge tier={diagnosis.liquidity?.tier} />
                    </div>
                    {diagnosis.liquidity?.current_ratio != null ? (
                        <p className="text-sm">Current ratio: <strong>{diagnosis.liquidity.current_ratio}</strong></p>
                    ) : diagnosis.liquidity?.net_current_assets != null ? (
                        <p className="text-sm">Net current assets: <strong>{fmtGBP(diagnosis.liquidity.net_current_assets)}</strong></p>
                    ) : (
                        <p className="text-sm text-muted-foreground">No balance-sheet data available.</p>
                    )}
                </div>

                <div className="rounded-md border p-3" data-testid="diagnosis-structural">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-muted-foreground">Structural</span>
                        <TierBadge tier={diagnosis.structural?.tier} />
                    </div>
                    {diagnosis.structural?.net_assets != null ? (
                        <p className="text-sm">Net assets: <strong>{fmtGBP(diagnosis.structural.net_assets)}</strong></p>
                    ) : (
                        <p className="text-sm text-muted-foreground">No balance-sheet data available.</p>
                    )}
                    {diagnosis.structural?.long_term_debt_ratio != null && (
                        <p className="text-xs text-muted-foreground mt-1">Long-term debt / net assets: {diagnosis.structural.long_term_debt_ratio}×</p>
                    )}
                </div>

                <div className="rounded-md border p-3" data-testid="diagnosis-profitability">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-muted-foreground">Profitability</span>
                        <TierBadge tier={diagnosis.profitability?.tier} />
                    </div>
                    <p className="text-sm text-muted-foreground">
                        {diagnosis.profitability?.tier === "insufficient_data"
                            ? diagnosis.profitability.note
                            : "Based on narrative commentary only — see flags below."}
                    </p>
                </div>
            </div>

            <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">
                    Red flags {diagnosis.red_flags?.length ? `(${diagnosis.red_flags.length})` : "— none found"}
                </p>
                {diagnosis.red_flags?.length > 0 && (
                    <div className="space-y-2">
                        {diagnosis.red_flags.map((flag, i) => (
                            <div key={i} className="rounded-md border p-3 bg-amber-50/50" data-testid={`red-flag-${i}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline">{RED_FLAG_LABELS[flag.category] || flag.category}</Badge>
                                </div>
                                <p className="text-sm">{flag.summary}</p>
                                <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                                    <Quote className="h-3 w-3 mt-0.5 shrink-0" />
                                    <span className="italic">"{flag.quote}"</span>
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DistressWatchlist() {
    const nav = useNavigate();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [companyNumber, setCompanyNumber] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [note, setNote] = useState("");
    const [adding, setAdding] = useState(false);
    const [refreshingId, setRefreshingId] = useState(null);
    const [diagnosingId, setDiagnosingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [promotingId, setPromotingId] = useState(null);

    const loadWatchlist = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/distressed/watchlist");
            setEntries(data.watchlist || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadWatchlist();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addCompany = async () => {
        if (!companyNumber.trim() || !companyName.trim()) {
            toast.error("Company number and name are both required.");
            return;
        }
        setAdding(true);
        try {
            const { data } = await api.post("/distressed/watchlist", {
                company_number: companyNumber.trim(),
                company_name: companyName.trim(),
                note: note.trim() || null,
            });
            setEntries((rows) => [data, ...rows]);
            setAddOpen(false);
            setCompanyNumber("");
            setCompanyName("");
            setNote("");
            toast.success(`${data.company_name} added to the watchlist.`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setAdding(false);
        }
    };

    const refreshEntry = async (id) => {
        setRefreshingId(id);
        try {
            const { data } = await api.post(`/distressed/watchlist/${id}/refresh`);
            setEntries((rows) => rows.map((r) => (r.id === id ? data : r)).sort(
                (a, b) => (b.severity_score || 0) - (a.severity_score || 0)
            ));
            toast.success(`${data.company_name} refreshed.`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setRefreshingId(null);
        }
    };

    const diagnoseEntry = async (id) => {
        setDiagnosingId(id);
        try {
            const { data } = await api.post(`/distressed/watchlist/${id}/diagnose`);
            setEntries((rows) => rows.map((r) => (r.id === id ? data : r)));
            setExpandedId(id);
            if (data.diagnosis?.accounts_available) {
                toast.success(`${data.company_name} diagnosis complete.`);
            } else {
                toast.warning(data.diagnosis?.error || "Diagnosis could not be completed.");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setDiagnosingId(null);
        }
    };

    const promoteEntry = async (id) => {
        setPromotingId(id);
        try {
            const { data } = await api.post(`/distressed/watchlist/${id}/promote`);
            toast.success(`Promoted to mandate — ${data.company_name}`);
            nav(`/distressed/mandates/${data.id}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setPromotingId(null);
        }
    };

    const removeEntry = async (id) => {
        try {
            await api.delete(`/distressed/watchlist/${id}`);
            setEntries((rows) => rows.filter((r) => r.id !== id));
            toast.success("Removed from watchlist.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <div className="p-6 space-y-6" data-testid="distress-watchlist-page">
            <PageHeader
                title="Distress Watchlist"
                description="Per-company Companies House and Gazette monitoring — deterministic severity scoring, not AI."
            />

            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground max-w-2xl">
                    Signals and scores here are computed from documented Companies House and
                    The Gazette rules. They flag things worth checking, not a professional
                    diagnosis — always verify a critical signal directly before acting on it.
                </p>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-watchlist-company-button">
                            <Plus className="h-4 w-4 mr-1" /> Add company
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Add company to watchlist</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Company number</Label>
                                <Input
                                    value={companyNumber}
                                    onChange={(e) => setCompanyNumber(e.target.value)}
                                    placeholder="e.g. 11428784"
                                    data-testid="watchlist-company-number-input"
                                />
                            </div>
                            <div>
                                <Label>Company name</Label>
                                <Input
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    placeholder="e.g. EXAMPLE TRADING LIMITED"
                                    data-testid="watchlist-company-name-input"
                                />
                            </div>
                            <div>
                                <Label>Note (optional)</Label>
                                <Input value={note} onChange={(e) => setNote(e.target.value)} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={addCompany} disabled={adding} data-testid="watchlist-add-submit">
                                {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                                Add & run first check
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading watchlist…
                </div>
            ) : entries.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        No companies on the watchlist yet. Add one to start monitoring.
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {entries.map((entry) => (
                        <Card key={entry.id} data-testid={`watchlist-entry-${entry.company_number}`}>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <div>
                                    <CardTitle className="text-base flex items-center gap-2">
                                        {entry.company_name}
                                        <TrajectoryBadge trajectory={entry.trajectory} />
                                    </CardTitle>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {entry.company_number} · Score {entry.severity_score}/100 · Last checked {fmtDate(entry.last_refreshed_at)}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm" variant="outline"
                                        onClick={() => refreshEntry(entry.id)}
                                        disabled={refreshingId === entry.id}
                                        data-testid={`refresh-watchlist-${entry.company_number}`}
                                    >
                                        {refreshingId === entry.id
                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                            : <RefreshCw className="h-4 w-4" />}
                                    </Button>
                                    <Button
                                        size="sm" variant="outline"
                                        onClick={() => diagnoseEntry(entry.id)}
                                        disabled={diagnosingId === entry.id}
                                        data-testid={`diagnose-watchlist-${entry.company_number}`}
                                        title="Run rapid diagnosis (fetches latest filed accounts + AI red-flag extraction — not instant)"
                                    >
                                        {diagnosingId === entry.id
                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                            : <Stethoscope className="h-4 w-4" />}
                                    </Button>
                                    {entry.diagnosis && (
                                        <Button
                                            size="sm" variant="ghost"
                                            onClick={() => setExpandedId((cur) => (cur === entry.id ? null : entry.id))}
                                            data-testid={`toggle-diagnosis-${entry.company_number}`}
                                        >
                                            {expandedId === entry.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                        </Button>
                                    )}
                                    <Button
                                        size="sm" variant="outline"
                                        onClick={() => promoteEntry(entry.id)}
                                        disabled={promotingId === entry.id}
                                        data-testid={`promote-watchlist-${entry.company_number}`}
                                        title="Promote to a distressed mandate"
                                    >
                                        {promotingId === entry.id
                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                            : <Briefcase className="h-4 w-4" />}
                                        <span className="ml-1.5 hidden sm:inline">Promote</span>
                                    </Button>
                                    <Button
                                        size="sm" variant="ghost"
                                        onClick={() => removeEntry(entry.id)}
                                        data-testid={`remove-watchlist-${entry.company_number}`}
                                    >
                                        <Trash2 className="h-4 w-4 text-red-600" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {entry.last_refresh_error && (
                                    <p className="text-xs text-amber-700 flex items-center gap-1 mb-2">
                                        <AlertTriangle className="h-3 w-3" /> {entry.last_refresh_error}
                                    </p>
                                )}
                                <p className="text-sm">{entry.severity_reason}</p>
                                {entry.note && (
                                    <p className="text-xs text-muted-foreground mt-2">Note: {entry.note}</p>
                                )}
                                {diagnosingId === entry.id && (
                                    <div className="mt-3 border-t pt-3 flex items-center gap-2 text-sm text-muted-foreground" data-testid="diagnosis-loading">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Fetching latest filed accounts and running red-flag extraction — this can take a moment…
                                    </div>
                                )}
                                {expandedId === entry.id && diagnosingId !== entry.id && (
                                    <DiagnosisPanel diagnosis={entry.diagnosis} />
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
